import { forwardRef, useState } from 'react'
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  MouseEvent,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { PiEye, PiEyeSlash } from 'react-icons/pi'
import './redesigns.css'

/**
 * The design's form primitives, drawn with the same tokens as the pages:
 * bordered fields on the surface, an accent primary button, uppercase tracked
 * labels. They exist so the sub-page content is the same product as the pages,
 * not an Ecme island inside the new shell.
 */

const FIELD =
  'w-full border bg-[var(--rda-surface)] px-3 py-2.5 text-base text-[var(--rda-on-surface)] ' +
  'outline-none placeholder:text-[color:var(--rda-muted)] transition-colors ' +
  'focus:border-[color:var(--rda-accent-text)] focus:ring-1 focus:ring-[color:var(--rda-accent-text)]'

function fieldBorder(invalid?: boolean) {
  return invalid ? 'border-[#DC2626]' : 'border-[color:var(--rda-line)]'
}

export function FormField({
  label,
  required,
  error,
  children,
}: {
  label?: string
  required?: boolean
  error?: string | null
  children: ReactNode
}) {
  return (
    <div className="py-2">
      {label ? (
        <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-[var(--rda-muted)]">
          {label}
          {required ? <span className="text-[#DC2626]"> *</span> : null}
        </label>
      ) : null}
      <div className={label ? 'mt-1.5' : ''}>{children}</div>
      {error ? <p className="mt-1 text-xs text-[#DC2626]">{error}</p> : null}
    </div>
  )
}

export const TextField = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function TextField({ className = '', invalid, ...rest }, ref) {
  return (
    <input ref={ref} className={FIELD + ' ' + fieldBorder(invalid) + ' ' + className} {...rest} />
  )
})

/**
 * A `TextField` with a show/hide toggle, the design's drop-in for the classic
 * `components/shared/PasswordInput`. The toggle sits inside the same bordered
 * field rather than a separate button, so it reads as one control.
 */
export const PasswordField = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { invalid?: boolean }
>(function PasswordField({ className = '', invalid, ...rest }, ref) {
  const [visible, setVisible] = useState(false)

  const toggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setVisible((v) => !v)
  }

  return (
    <div className="relative">
      <input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={FIELD + ' ' + fieldBorder(invalid) + ' pr-10 ' + className}
        {...rest}
      />
      <button
        type="button"
        aria-label={visible ? 'Hide password' : 'Show password'}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-[var(--rda-muted)] outline-none focus-visible:text-[var(--rda-accent-text)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-current"
        onClick={toggle}
      >
        {visible ? <PiEyeSlash aria-hidden /> : <PiEye aria-hidden />}
      </button>
    </div>
  )
})

export const TextAreaField = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function TextAreaField({ className = '', invalid, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      className={FIELD + ' ' + fieldBorder(invalid) + ' resize-none ' + className}
      {...rest}
    />
  )
})

export const SelectField = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & {
    invalid?: boolean
    placeholder?: string
    options: string[]
  }
>(function SelectField({ className = '', invalid, placeholder, options, ...rest }, ref) {
  return (
    <select
      ref={ref}
      className={FIELD + ' ' + fieldBorder(invalid) + ' ' + className}
      {...rest}
    >
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  )
})

export function RadioField({
  value,
  options,
  onChange,
}: {
  value: string
  options: string[]
  onChange: (v: string) => void
}) {
  return (
    <div className="py-1">
      {options.map((o) => (
        <label key={o} className="flex cursor-pointer items-center gap-2.5 py-1.5">
          <input
            type="radio"
            checked={value === o}
            className="h-4 w-4 accent-[var(--rda-accent)]"
            onChange={() => onChange(o)}
          />
          <span className="text-sm">{o}</span>
        </label>
      ))}
    </div>
  )
}

export function PrimaryButton({
  children,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode }) {
  return (
    <button
      className={
        'rda-tile flex w-full items-center justify-center gap-2 bg-[var(--rda-accent)] px-4 py-3 ' +
        'text-sm font-semibold text-[var(--rda-on-accent)] disabled:opacity-50 ' +
        className
      }
      {...rest}
    >
      {children}
    </button>
  )
}

export function SecondaryButton({
  children,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode }) {
  return (
    <button
      className={
        'rda-tile flex items-center justify-center gap-2 border border-[color:var(--rda-line)] ' +
        'bg-[var(--rda-surface)] px-4 py-2.5 text-sm font-semibold text-[var(--rda-on-surface)] ' +
        className
      }
      {...rest}
    >
      {children}
    </button>
  )
}

export function InfoNote({ children }: { children: ReactNode }) {
  return (
    <div className="bg-[var(--rda-neutral)] px-3 py-2.5 text-sm text-[var(--rda-on-neutral)]">
      {children}
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div className="bg-[#FCE8E6] px-3 py-2.5 text-sm text-[#7A1F17]">{children}</div>
  )
}
